import React, { useState } from 'react';
import { usePortalAuth } from '../context/PortalAuthContext';
import { usePortalTitle } from '../hooks/usePortalTitle';
import { BrandMark } from '../../components/BrandMark';

const PortalLogin: React.FC = () => {
  usePortalTitle('Login');
  const [clientUserName, setClientUserName] = useState('');
  const [clientUserPassword, setClientUserPassword] = useState('');
  const [error, setError] = useState('');
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login } = usePortalAuth();

  const validateForm = (): boolean => {
    let isValid = true;
    setUsernameError('');
    setPasswordError('');
    if (!clientUserName.trim()) {
      setUsernameError('Username is required');
      isValid = false;
    }
    if (!clientUserPassword) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (clientUserPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!validateForm()) return;
    setIsLoading(true);
    try {
      await login({ clientUserName: clientUserName.trim(), clientUserPassword });
    } catch (err: unknown) {
      const message =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      setError(message || 'Invalid username or password');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex app-mesh-bg">
      <aside className="hidden lg:flex lg:w-[42%] relative overflow-hidden auth-panel-glow text-white flex-col justify-between p-12">
        <div className="relative z-10 flex items-center gap-3">
          <BrandMark size="md" inverted />
          <div>
            <p className="font-display text-xl font-bold tracking-tight">Client Portal</p>
            <p className="text-sm text-teal-100/80">Unibyte</p>
          </div>
        </div>
        <div className="relative z-10 max-w-sm animate-slide-up">
          <h1 className="font-display text-3xl font-bold leading-tight tracking-tight">
            Your work, invoices, and support — in one place.
          </h1>
          <p className="mt-4 text-sm text-slate-200/85 leading-relaxed">
            Sign in to view services, track tickets, and manage billing.
          </p>
        </div>
        <p className="relative z-10 text-xs text-teal-100/50">
          © {new Date().getFullYear()} Unibyte
        </p>
      </aside>

      <main className="flex-1 flex items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-[400px] animate-slide-up">
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <BrandMark size="md" />
            <p className="font-display text-lg font-bold text-slate-900">Client Portal</p>
          </div>

          <div className="mb-8">
            <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900">Sign in</h2>
            <p className="mt-1.5 text-sm text-slate-500">Access your client account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-xl bg-rose-50 border border-rose-200/80 p-3 flex items-center gap-2">
                <svg className="h-5 w-5 text-rose-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm font-medium text-rose-800">{error}</p>
              </div>
            )}
            <div>
              <label htmlFor="clientUserName" className="block text-sm font-medium text-slate-700 mb-1.5">
                Username
              </label>
              <input
                id="clientUserName"
                type="text"
                autoComplete="username"
                value={clientUserName}
                onChange={(e) => {
                  setClientUserName(e.target.value);
                  if (usernameError) setUsernameError('');
                }}
                placeholder="Enter your username"
                disabled={isLoading}
                className={`block w-full px-3.5 py-2.5 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400 text-sm shadow-sm focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600 focus:outline-none transition-shadow ${
                  usernameError ? 'border-rose-300' : 'border-slate-200'
                }`}
              />
              {usernameError && <p className="mt-1.5 text-sm text-rose-600">{usernameError}</p>}
            </div>
            <div>
              <label htmlFor="clientUserPassword" className="block text-sm font-medium text-slate-700 mb-1.5">
                Password
              </label>
              <input
                id="clientUserPassword"
                type="password"
                autoComplete="current-password"
                value={clientUserPassword}
                onChange={(e) => {
                  setClientUserPassword(e.target.value);
                  if (passwordError) setPasswordError('');
                }}
                placeholder="Enter your password"
                disabled={isLoading}
                className={`block w-full px-3.5 py-2.5 rounded-xl border bg-white text-slate-900 placeholder:text-slate-400 text-sm shadow-sm focus:ring-2 focus:ring-teal-600/30 focus:border-teal-600 focus:outline-none transition-shadow ${
                  passwordError ? 'border-rose-300' : 'border-slate-200'
                }`}
              />
              {passwordError && <p className="mt-1.5 text-sm text-rose-600">{passwordError}</p>}
            </div>
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-2.5 px-4 rounded-xl font-semibold text-sm text-white bg-teal-600 hover:bg-teal-700 focus:ring-2 focus:ring-offset-2 focus:ring-teal-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-soft"
            >
              {isLoading ? (
                <span className="inline-flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Signing in…
                </span>
              ) : (
                'Sign in'
              )}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PortalLogin;
